import React, { useState } from "react";
import { Modal } from "../common/Modal";
import { Answer } from "../../types";
import { Trash2, Archive, AlertTriangle, Loader2, Trophy, Sparkles } from "lucide-react";

export interface DeleteAnswerModalProps {
  isOpen: boolean;
  onClose: () => void;
  answer: Answer | null;
  onConfirmDelete: (id: string) => Promise<void>;
  onRetireInstead?: (answer: Answer) => Promise<void>;
}

export const DeleteAnswerModal: React.FC<DeleteAnswerModalProps> = ({
  isOpen,
  onClose,
  answer,
  onConfirmDelete,
  onRetireInstead,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDelete = async () => {
    if (!answer) return;
    setSubmitting(true);
    setError(null);
    try {
      await onConfirmDelete(answer.id);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete answer.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleRetire = async () => {
    if (!answer || !onRetireInstead) return;
    setSubmitting(true);
    setError(null);
    try {
      await onRetireInstead(answer);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to retire answer.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      maxWidth="md"
      title="Delete Library Answer"
      description="Permanent removal confirmation."
    >
      <div className="space-y-4">
        {error && (
          <div className="rounded-lg bg-rose-50 p-3 text-xs font-semibold text-rose-700 dark:bg-rose-950/50 dark:text-rose-300">
            {error}
          </div>
        )}

        <div className="rounded-xl border border-rose-200 bg-rose-50/70 p-3.5 text-xs text-rose-900 dark:border-rose-900/40 dark:bg-rose-950/30 dark:text-rose-200 flex items-start gap-2.5">
          <AlertTriangle className="h-4 w-4 text-rose-600 shrink-0 mt-0.5" />
          <div className="space-y-1.5">
            <span className="font-bold text-sm block text-slate-900 dark:text-slate-100">
              {answer?.title}
            </span>
            <p className="text-rose-700 dark:text-rose-300 leading-relaxed">
              Are you sure you want to permanently delete this answer from the Approved Answer
              Library? This action cannot be undone.
            </p>
          </div>
        </div>

        {answer && (answer.wonBidCount || 0) > 0 && (
          <div className="flex items-center gap-2 rounded-lg bg-amber-50 p-2.5 text-xs font-medium text-amber-800 border border-amber-200 dark:bg-amber-950/40 dark:border-amber-900 dark:text-amber-300">
            <Trophy className="h-4 w-4 text-amber-500 shrink-0" />
            <span>
              This answer has been credited in {answer.wonBidCount} winning proposal
              {answer.wonBidCount === 1 ? "" : "s"}. Deleting it will permanently remove this
              library entry from future matching.
            </span>
          </div>
        )}

        {answer?.harvestedFrom && (
          <div className="flex items-center gap-2 rounded-lg bg-teal-50 p-2.5 text-xs font-medium text-teal-800 border border-teal-200 dark:bg-teal-950/40 dark:border-teal-900 dark:text-teal-300">
            <Sparkles className="h-4 w-4 text-[#2ba5b5] shrink-0" />
            <span>
              Harvested from {answer.harvestedFrom.clientName} (
              {answer.harvestedFrom.opportunityTitle})
            </span>
          </div>
        )}

        <p className="text-xs text-slate-500 dark:text-slate-400">
          Tip: If you simply want to prevent this answer from being recommended in active drafting
          while preserving provenance and win records, consider <strong>Retiring</strong> it
          instead.
        </p>

        <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-2 pt-3 border-t border-slate-100 dark:border-slate-800">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="rounded-lg px-3.5 py-2 text-xs font-medium text-slate-600 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800 disabled:opacity-50"
          >
            Cancel
          </button>

          <div className="flex items-center gap-2">
            {onRetireInstead && answer?.status !== "retired" && (
              <button
                type="button"
                onClick={handleRetire}
                disabled={submitting}
                className="btn-animate inline-flex items-center gap-1.5 rounded-lg border border-amber-300 bg-amber-50 px-3.5 py-2 text-xs font-semibold text-amber-800 hover:bg-amber-100 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200 disabled:opacity-50"
              >
                <Archive className="h-3.5 w-3.5 text-amber-600" />
                <span>Retire Instead</span>
              </button>
            )}

            <button
              type="button"
              onClick={handleDelete}
              disabled={submitting}
              className="btn-animate inline-flex items-center gap-1.5 rounded-lg bg-rose-600 px-4 py-2 text-xs font-semibold text-white hover:bg-rose-700 disabled:opacity-50"
            >
              {submitting ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Trash2 className="h-3.5 w-3.5" />
              )}
              <span>{submitting ? "Deleting..." : "Delete Permanently"}</span>
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};
